import React from "react";
import { Alert, Box, Button, Link, Typography } from "@mui/material";
import BaseModal from "./BaseModal";
import FormField from "../forms/FormField";
import ShopifyCreateCustomer from "../../utils/ShopifyCreateCustomer";
import ShopifyCreateCustomerAccessToken from "../../utils/ShopifyCreateCustomerAccessToken";
import CheckboxField from "../forms/CheckboxField";
import BaseDrawer from "./mobile/BaseDrawer";

const SignUpModal = ({ onClose, setModal, modal, isMobile, data }) => {
  const { initCheckout, prevType } = modal;
  const Modal = isMobile ? BaseDrawer : BaseModal;

  const [form, setForm] = React.useState({
    values: {
      email: "",
      password: "",
      terms: false,
    },
    valid: false,
    loading: false,
  });
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (form.values.email && form.values.password && form.values.terms) {
      setForm((state) => ({
        ...state,
        valid: true,
      }));
    } else {
      setForm((state) => ({
        ...state,
        valid: false,
      }));
    }
  }, [form.values]);

  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    setForm((state) => ({
      ...state,
      values: {
        ...state.values,
        [name]: name === "terms" ? checked : value,
      },
    }));
  };

  const handleSignUp = () => {
    const getLoadingState = (loading = false) => {
      setForm((state) => ({
        ...state,
        loading,
      }));
    };

    setError(null);
    getLoadingState(true);

    ShopifyCreateCustomer(form.values.email, form.values.password)
      .then((customerValue) => {
        if (!customerValue) {
          setError("Sorry, an account with that email or password already exists.");
          return;
        }

        const { customerUserErrors: customerErrors } = customerValue;

        if (customerErrors.length) {
          switch (customerErrors[0].code) {
            case "INVALID":
              setError("Sorry, that email address isn't valid.");
              break;
            case "TAKEN":
              setError("Sorry, an account with that email already exists.");
              break;
            case "TOO_SHORT":
              setError("Sorry, that password is too short.");
              break;
            default:
              setError("Sorry, something went wrong. Please try again.");
          }
        } else {
          ShopifyCreateCustomerAccessToken(form.values.email, form.values.password).then(
            ({ customerUserErrors: tokenErrors, customerAccessToken }) => {
              if (tokenErrors.length) {
                setError("Sorry, something went wrong. Please try again.");
              } else {
                sessionStorage.setItem("sup_token", customerAccessToken.accessToken);
                data.setToken(customerAccessToken.accessToken);

                const type = prevType === "SAVE_DESIGN";

                if (initCheckout && !type) {
                  setModal({ type: "ATB_DISCLAIMER" });
                } else if (type) {
                  setModal({ type: "SAVE_DESIGN" });
                } else {
                  onClose();
                }
              }
            }
          );
        }
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        getLoadingState();
      });
  };

  return (
    <Modal headline="Sign Up" onClose={onClose}>
      {error && (
        <Alert severity="error" sx={{ marginBottom: "20px" }}>
          {error}
        </Alert>
      )}
      <FormField
        label="Email Address"
        name="email"
        handleChange={handleChange}
        isKiosk={data.isKiosk}
      />
      <FormField
        label="Password"
        type="password"
        name="password"
        handleChange={handleChange}
        isKiosk={data.isKiosk}
      />
      <CheckboxField name="terms" handleChange={handleChange}>
        I agree to all statements included in the{" "}
        <a
          target="_blank"
          href={`https://${process.env.REACT_APP_SHOP_URL}/policies/terms-of-service`}
          rel="noreferrer"
          style={{ fontWeight: "700" }}
        >
          terms of service
        </a>
      </CheckboxField>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Button
          variant="contained"
          sx={{
            width: "100%",
            maxWidth: "280px",
          }}
          disabled={form.loading || !form.valid}
          onClick={handleSignUp}
        >
          {form.loading ? "Signing up..." : "Sign Up"}
        </Button>
        <Typography sx={{ margin: "15px 0px" }}>OR</Typography>
        <Typography>
          Already have an account?{" "}
          <Link onClick={() => setModal({ type: "SIGN_IN", initCheckout })}>Sign in here</Link>
        </Typography>
      </Box>
    </Modal>
  );
};

export default SignUpModal;
